import { Button, Grid, Paper } from '@mui/material';
import { DoneAll } from '@mui/icons-material';
import { useState } from 'react';
import { toast } from 'react-toastify';
import MainContentHeader from 'src/shared/components/atoms/mainContentHeader';
import GridWrapper from 'src/shared/components/organisms/gridWrapper';
import ConsoleHelper from 'src/shared/functions/consoleHelper';
import { MemberUpdateActionEnum } from 'src/shared/models/enums/memberUpdateAction.enum';
import { createMemberUpdate, getPendingMemberUpdateIds } from 'src/shared/services/memberUpdateService';
import { useTranslation } from 'react-i18next';
import { useIsMounted } from 'src/shared/hooks/useIsMounted';
import { MemberUpdateApproveAll } from '../components/memberUpdateApproveAll';
import { MemberUpdateConfirm } from '../components/memberUpdateConfirm';
import { MemberUpdateDataGrid } from '../components/memberUpdateDataGrid';
import { MemberUpdateModal } from '../components/memberUpdateModal';
import { MemberUpdateRequest } from '../models/memberUpdateRequest';

export const MemberUpdates = () => {
  const { t } = useTranslation(['member', 'common']);
  const isMounted = useIsMounted();
  const [loading, setLoading] = useState<boolean>(false);
  const [showConfirm, setShowConfirm] = useState<boolean>(false);
  const [showMemberUpdateModal, setShowMemberUpdateModal] = useState<boolean>(false);
  const [abortMode, setAbortMode] = useState<boolean>(false);
  const [selectedMemberIds, setSelectedMemberIds] = useState<number[]>([]);
  const [selectedMemberUpdateId, setSelectedMemberUpdateId] = useState<number>();
  const [approveAllIds, setApproveAllIds] = useState<number[] | undefined>(undefined);
  const [fetchingAll, setFetchingAll] = useState<boolean>(false);

  // Godkänn alla gäller samtliga väntande uppdateringar användaren har behörighet till, inte bara
  // de som syns på sidan. Servern avgränsar listan på förening eller distrikt.
  const handleApproveAllClicked = async () => {
    setFetchingAll(true);

    try {
      const result = await getPendingMemberUpdateIds();

      if (!isMounted()) return;

      if (result.data.length === 0) {
        toast.info(t('member:memberUpdate.approveAllNone') as string);
        return;
      }

      setApproveAllIds(result.data);
    } catch (e) {
      if (isMounted()) ConsoleHelper.error(e);
    } finally {
      if (isMounted()) setFetchingAll(false);
    }
  };

  const handleConfirmClicked = (memberIds: number[], newAbortMode: boolean) => {
    setSelectedMemberIds(memberIds);
    setShowConfirm(true);
    setAbortMode(newAbortMode);
    ConsoleHelper.log('handleConfirmClicked', memberIds, abortMode);
  };

  const handleMemberUpdate = async () => {
    setLoading(true);
    const requestData: MemberUpdateRequest[] = [];
    selectedMemberIds.forEach((item) => {
      requestData.push({
        memberUpdate_id: item,
        action: abortMode ? MemberUpdateActionEnum.Reject : MemberUpdateActionEnum.Accept,
      });
    });
    try {
      await createMemberUpdate(requestData);
      if (isMounted()) {
        toast.success(<>{abortMode ? t('member:memberUpdate.updateRejected') : t('member:memberUpdate.updateSuccess')}</>);
        setSelectedMemberIds([]);
      }
    } catch (e) {
      if (isMounted()) ConsoleHelper.error(e);
    } finally {
      if (isMounted()) setLoading(false);
    }
  };

  const handleReviewClicked = (id: number) => {
    setSelectedMemberUpdateId(id);
    setShowMemberUpdateModal(true);
  };

  return (
    <>
      <GridWrapper>
        <Grid item xs={12} sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 1 }}>
          <MainContentHeader title={t('member:memberUpdate.reviewUpdatesHeader')} />
          <Button
            variant='contained'
            disableElevation
            startIcon={<DoneAll fontSize='small' />}
            disabled={fetchingAll || loading}
            onClick={handleApproveAllClicked}
          >
            {t('member:memberUpdate.approveAll')}
          </Button>
        </Grid>
        <Grid item xs={12}>
          <Paper>
            {!loading && (
              <MemberUpdateDataGrid
                loadingCallback={() => {
                  // setLoading(loadingStatus);
                }}
                reviewClicked={handleReviewClicked}
                confirmClicked={handleConfirmClicked}
                checkboxSelection
              />
            )}
          </Paper>
        </Grid>
      </GridWrapper>
      {showConfirm && selectedMemberIds && !!selectedMemberIds.length && (
        <MemberUpdateConfirm
          callback={() => {
            setShowConfirm(false);
            handleMemberUpdate();
          }}
          onClose={() => {
            setShowConfirm(false);
            setSelectedMemberIds([]);
          }}
          abortMode={abortMode}
        />
      )}
      {approveAllIds && (
        <MemberUpdateApproveAll
          memberUpdateIds={approveAllIds}
          onClose={() => setApproveAllIds(undefined)}
          onDone={() => {
            setApproveAllIds(undefined);
            // Samma knep som efter en vanlig godkänning, griden monteras om och hämtar nytt.
            setLoading(true);
            setTimeout(() => setLoading(false), 0);
          }}
        />
      )}
      {selectedMemberUpdateId && (
        <MemberUpdateModal
          modalOpen={showMemberUpdateModal}
          memberUpdateId={selectedMemberUpdateId}
          onSave={() => setShowMemberUpdateModal(false)}
          onClose={() => setShowMemberUpdateModal(false)}
        />
      )}
    </>
  );
};
